import { useCallback, useEffect, useState } from "react";
import {
  fetchFoodRetailPackageReadiness,
  runFoodAraPackageParsingBackfill,
} from "../../../../api/foodApi";
import {
  foodRetailSourceLabel,
  isFoodRetailPlaybookSource,
} from "../../../../constants/foodSourceConstants";
import AdminTableContainer from "../layout/AdminTableContainer";
import { formatCopPrice } from "./foodRetailIngestionHelpers";

const FoodAraPackageReadinessSection = ({ sourceKey }) => {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmRealBackfill, setConfirmRealBackfill] = useState(false);
  const sourceLabel = foodRetailSourceLabel(sourceKey);

  const loadReadiness = useCallback(async () => {
    if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
      setReadiness(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      const payload = await fetchFoodRetailPackageReadiness({
        sourceKey,
        limit: 200,
      });
      setReadiness(payload);
    } catch (error) {
      setErrorMessage(error.message || "Failed to load package readiness.");
      setReadiness(null);
    } finally {
      setLoading(false);
    }
  }, [sourceKey]);

  useEffect(() => {
    loadReadiness();
  }, [loadReadiness]);

  const runBackfill = async (dryRun) => {
    if (!sourceKey) return;
    if (!dryRun && !confirmRealBackfill) {
      setBackfillMessage("Confirm real backfill before applying changes.");
      return;
    }

    try {
      setBackfillRunning(true);
      setBackfillMessage("");
      const result = await runFoodAraPackageParsingBackfill({
        sourceKey,
        dryRun,
        overwrite: false,
        limit: 500,
      });
      setBackfillMessage(
        dryRun
          ? `Dry run: ${result.rowsUpdated} row(s) would update (${result.rowsWithPackageFound} with package found).`
          : `Backfill applied: ${result.rowsUpdated} row(s) updated.`
      );
      await loadReadiness();
    } catch (error) {
      setBackfillMessage(error.message || "Backfill failed.");
    } finally {
      setBackfillRunning(false);
    }
  };

  const summary = readiness?.summary;
  const readyExamples = (readiness?.examples || []).filter((row) => row.status === "READY");
  const problemExamples = (readiness?.examples || []).filter((row) => row.status !== "READY");

  if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
    return null;
  }

  return (
    <section className="panel-card food-retail-ingestion-page__normalization">
      <div className="food-retail-ingestion-page__section-header">
        <div>
          <h2>{sourceLabel} Package Readiness</h2>
          <p className="muted">
            Package quantity, unit, and derivable unit price for {sourceLabel} observations. Use
            dry-run backfill before updating stored rows.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={loadReadiness} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}
      {backfillMessage ? <p className="muted">{backfillMessage}</p> : null}

      {summary ? (
        <div className="food-retail-ingestion-page__kpi-row">
          <div>
            <span className="muted">Reviewed</span>
            <strong>{summary.observationsReviewed}</strong>
          </div>
          <div>
            <span className="muted">With package</span>
            <strong>{summary.withPackageQuantity}</strong>
          </div>
          <div>
            <span className="muted">Unit price ready</span>
            <strong>{summary.withUnitPrice}</strong>
          </div>
          <div>
            <span className="muted">Missing package</span>
            <strong>{summary.missingPackage}</strong>
          </div>
          <div>
            <span className="muted">Incompatible unit</span>
            <strong>{summary.incompatibleUnit}</strong>
          </div>
        </div>
      ) : null}

      <div className="food-retail-ingestion-page__actions-row">
        <button
          type="button"
          className="btn btn-secondary"
          disabled={backfillRunning}
          onClick={() => runBackfill(true)}
        >
          {backfillRunning ? "Running…" : "Run package parse backfill (dry run)"}
        </button>
        <label className="checkbox-inline">
          <input
            type="checkbox"
            checked={confirmRealBackfill}
            onChange={(event) => setConfirmRealBackfill(event.target.checked)}
          />
          Confirm real backfill
        </label>
        <button
          type="button"
          className="btn btn-primary"
          disabled={backfillRunning || !confirmRealBackfill}
          onClick={() => runBackfill(false)}
        >
          Run package parse backfill
        </button>
      </div>

      {readyExamples.length ? (
        <>
          <h3>Ready examples</h3>
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Package</th>
                  <th>Price</th>
                  <th>Unit price</th>
                </tr>
              </thead>
              <tbody>
                {readyExamples.map((row) => (
                  <tr key={row.observationId}>
                    <td>{row.rawProductName}</td>
                    <td>
                      {row.packageQuantity} {row.packageUnit}
                    </td>
                    <td>{formatCopPrice(row.priceCop)}</td>
                    <td>{formatCopPrice(row.unitPriceCop)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}

      {problemExamples.length ? (
        <>
          <h3>Problem examples</h3>
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Status</th>
                  <th>Package</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {problemExamples.map((row) => (
                  <tr key={row.observationId}>
                    <td>{row.rawProductName}</td>
                    <td>{row.status}</td>
                    <td>
                      {row.packageQuantity ?? "—"} {row.packageUnit ?? ""}
                    </td>
                    <td>{formatCopPrice(row.priceCop)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}
    </section>
  );
};

export default FoodAraPackageReadinessSection;
